import { useCallback, useEffect, useState } from "react";

/**
 * Light and dark are built from the same semantic tokens (design.md section 3), so the toggle only
 * moves one attribute on the root element and every colour follows.
 *
 * The choice is a display preference, not authenticated data, so `localStorage` is the right home
 * for it and the rule in plan.md section 8 about authenticated data in browser storage is not in
 * play. Nothing here records who chose it.
 */

export type Theme = "light" | "dark";

export const ThemeToggleLabel: Record<Theme, string> = {
  light: "Byt till mörkt läge",
  dark: "Byt till ljust läge"
};

const STORAGE_KEY = "portal-theme";

function initialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }
  } catch {
    // A browser that refuses storage still gets a working portal.
  }

  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Ignored on purpose.
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  return { theme, toggleTheme };
}

/**
 * Ljust, Mörkt or System, the three choices design.md section 3 names, for Petrovic's own
 * Inställningar › Utseende since 2026-09-24.
 *
 * Until then the admin pages had no theme control at all and followed the operating system, because
 * `useTheme` is called only inside the client's shell. "System" is the absence of a stored choice, so
 * the document carries no stamp and the stylesheet's `prefers-color-scheme` block decides, exactly as
 * it did before anyone chose. The client's toggle and this read and write the same key, so a choice
 * made on one side holds on the other.
 */
export type ThemePreference = Theme | "system";

function storedPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }
  } catch {
    // A browser that refuses storage follows the system.
  }
  return "system";
}

export function useThemePreference() {
  const [preference, setPreference] = useState<ThemePreference>(storedPreference);

  useEffect(() => {
    try {
      if (preference === "system") {
        delete document.documentElement.dataset.theme;
        localStorage.removeItem(STORAGE_KEY);
      } else {
        document.documentElement.dataset.theme = preference;
        localStorage.setItem(STORAGE_KEY, preference);
      }
    } catch {
      // Ignored on purpose, as above.
    }
  }, [preference]);

  return { preference, setPreference };
}
